import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FaInfoCircle, FaCalendarAlt, FaClock, FaRedo, FaPlus } from 'react-icons/fa';
import taskService from '../services/taskService';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { toast } from 'react-hot-toast';

const FormContainer = styled.div`
  background-color: var(--semantic-surface, #ffffff);
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  border: 1px solid var(--semantic-border, #e5e7eb);
  transition: all 0.3s ease;

  html.dark & {
    background-color: var(--dark-100);
    border-color: var(--dark-300);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const FormHeader = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--semantic-text-primary, #333);
  font-weight: 600;

  html.dark & {
    color: var(--dark-900);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  color: var(--semantic-text-secondary, #6c757d);
  margin-bottom: 0.25rem;
  font-weight: 500;

  html.dark & {
    color: var(--dark-700);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--semantic-surface, #ffffff);
  color: var(--semantic-text-primary, #333);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500, #3b82f6);
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
  }

  &::placeholder {
    color: var(--semantic-text-tertiary, #9ca3af);
  }

  html.dark & {
    background-color: var(--dark-200);
    border-color: var(--dark-300);
    color: var(--dark-900);

    &:focus {
      border-color: var(--accent-500);
      box-shadow: 0 0 0 0.2rem rgba(124, 58, 237, 0.25);
    }

    &::placeholder {
      color: var(--dark-500);
    }
  }
`;

const SelectContainer = styled.div`
  position: relative;
  margin-top: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid var(--semantic-text-secondary, #6c757d);
    pointer-events: none;
  }

  html.dark &:after {
    border-top-color: var(--dark-700);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  font-size: 1rem;
  appearance: none;
  background-color: var(--semantic-surface, #ffffff);
  color: var(--semantic-text-primary, #333);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500, #3b82f6);
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
  }

  html.dark & {
    background-color: var(--dark-200);
    border-color: var(--dark-300);
    color: var(--dark-900);

    &:focus {
      border-color: var(--accent-500);
      box-shadow: 0 0 0 0.2rem rgba(124, 58, 237, 0.25);
    }
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ToggleLabel = styled.label`
  font-size: 1rem;
  color: var(--semantic-text-primary, #333);
  font-weight: 500;

  html.dark & {
    color: var(--dark-900);
  }
`;

const Toggle = styled.div`
  position: relative;
  width: 50px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? 'var(--primary-500, #3b82f6)' : 'var(--semantic-border, #ced4da)'};
  cursor: pointer;
  transition: background-color 0.2s;

  html.dark & {
    background-color: ${props => props.active ? 'var(--accent-500)' : 'var(--dark-300)'};
  }
`;

const ToggleButton = styled.div`
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '26px' : '2px'};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  html.dark & {
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
`;

// Enhanced DateTime Components
const DateTimeContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const DateTimeGroup = styled.div`
  flex: 1;
  min-width: 140px;
`;

const DatePickerContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DateTimeInput = styled.div`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--semantic-surface, #ffffff);
  color: var(--semantic-text-primary, #333);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-400, #60a5fa);
  }
  
  svg {
    margin-right: 8px;
    color: var(--primary-500, #3b82f6);
  }

  html.dark & {
    background-color: var(--dark-200);
    border-color: var(--dark-300);
    color: var(--dark-900);

    &:hover {
      border-color: var(--accent-400);
    }

    svg {
      color: var(--accent-500);
    }
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: var(--semantic-surface, #ffffff);
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;

  html.dark & {
    background-color: var(--dark-100);
    border-color: var(--dark-300);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  }
`;

const TimePickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  padding: 8px;
`;

const TimeOption = styled.div`
  padding: 8px;
  text-align: center;
  cursor: pointer;
  border-radius: 4px;
  color: var(--semantic-text-primary, #333);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary-50, #eff6ff);
  }
  
  ${props => props.selected && `
    background-color: var(--primary-500, #3b82f6);
    color: white;
    
    &:hover {
      background-color: var(--primary-600, #2563eb);
    }
  `}

  html.dark & {
    color: var(--dark-900);

    &:hover {
      background-color: var(--dark-200);
    }

    ${props => props.selected && `
      background-color: var(--accent-500);
      color: white;
      
      &:hover {
        background-color: var(--accent-600);
      }
    `}
  }
`;

const DurationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const DurationButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  background-color: var(--semantic-surface, #ffffff);
  color: var(--semantic-text-secondary, #6c757d);
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary-50, #eff6ff);
    border-color: var(--primary-300, #93c5fd);
    color: var(--primary-700, #1d4ed8);
  }

  html.dark & {
    border-color: var(--dark-300);
    background-color: var(--dark-200);
    color: var(--dark-700);

    &:hover {
      background-color: var(--dark-300);
      border-color: var(--accent-500);
      color: var(--accent-400);
    }
  }
`;

const RecurrenceOptionContainer = styled.div`
  margin-top: 1rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  padding: 1rem;
  background-color: var(--semantic-surface, #ffffff);

  html.dark & {
    border-color: var(--dark-300);
    background-color: var(--dark-200);
  }
`;

const RecurrenceHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: var(--semantic-text-primary, #333);
  font-weight: 500;
  
  svg {
    margin-right: 8px;
    color: var(--primary-500, #3b82f6);
  }

  html.dark & {
    color: var(--dark-900);

    svg {
      color: var(--accent-500);
    }
  }
`;

const RecurrenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 1rem;
`;

const RecurrenceOption = styled.div`
  padding: 10px;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  color: var(--semantic-text-primary, #333);
  background-color: var(--semantic-surface, #ffffff);
  transition: all 0.2s ease;
  
  ${props => props.selected && `
    border-color: var(--primary-500, #3b82f6);
    background-color: var(--primary-50, #eff6ff);
    color: var(--primary-700, #1d4ed8);
  `}
  
  &:hover {
    background-color: ${props => props.selected ? 'var(--primary-50, #eff6ff)' : 'var(--primary-25, #f8fafc)'};
  }

  html.dark & {
    border-color: var(--dark-300);
    background-color: var(--dark-100);
    color: var(--dark-900);

    ${props => props.selected && `
      border-color: var(--accent-500);
      background-color: var(--accent-500/10);
      color: var(--accent-400);
    `}

    &:hover {
      background-color: ${props => props.selected ? 'var(--accent-500/10)' : 'var(--dark-200)'};
    }
  }
`;

// Custom recurrence option components
const CustomRecurrenceForm = styled.div`
  margin-top: 1rem;
`;

const ICALResult = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: var(--primary-50, #eff6ff);
  border-radius: 4px;
  border: 1px solid var(--semantic-border, #ced4da);
  font-family: monospace;
  font-size: 0.875rem;
  color: var(--semantic-text-primary, #333);

  html.dark & {
    background-color: var(--dark-200);
    border-color: var(--dark-300);
    color: var(--dark-800);
  }
`;

const HelpText = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: var(--semantic-text-secondary, #6c757d);
  margin-top: 0.5rem;
  
  svg {
    margin-right: 0.25rem;
  }

  html.dark & {
    color: var(--dark-600);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  font-weight: 500;
  
  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: var(--semantic-text-secondary, #6c757d);
  border: 1px solid var(--semantic-border, #ced4da);
  
  &:hover:not(:disabled) {
    background-color: var(--primary-50, #eff6ff);
    border-color: var(--primary-300, #93c5fd);
  }

  html.dark & {
    color: var(--dark-700);
    border-color: var(--dark-300);

    &:hover:not(:disabled) {
      background-color: var(--dark-200);
      border-color: var(--dark-400);
    }
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary-600, #2563eb);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-700, #1d4ed8);
  }

  &:active:not(:disabled) {
    background-color: var(--primary-800, #1e40af);
  }

  html.dark & {
    background-color: var(--accent-500);
    
    &:hover:not(:disabled) {
      background-color: var(--accent-600);
      box-shadow: 0 0 20px rgba(124, 58, 237, 0.3);
    }

    &:active:not(:disabled) {
      background-color: var(--accent-700);
    }
  }
`;

// Common recurrence options
const RECURRENCE_PRESETS = [
  { label: 'Daily', value: 'FREQ=DAILY' },
  { label: 'Weekdays', value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR' },
  { label: 'Weekly', value: 'FREQ=WEEKLY' },
  { label: 'Monthly', value: 'FREQ=MONTHLY' },
  { label: 'Annually', value: 'FREQ=YEARLY' },
  { label: 'Custom', value: 'custom' }
];

// Add a custom hook for detecting clicks outside an element
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
`;

const ColorDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color || '#ccc'};
  border: 1px solid var(--semantic-border, #e5e7eb);

  html.dark & {
    border-color: var(--dark-300);
  }
`;

const CreateCategoryOption = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-600, #2563eb);
  padding: 4px 0;
  
  svg {
    margin-right: 8px;
  }

  html.dark & {
    color: var(--accent-500);
  }
`;

const NewCategoryForm = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  background-color: var(--semantic-surface, #ffffff);

  html.dark & {
    border-color: var(--dark-300);
    background-color: var(--dark-100);
  }
`;

const ColorPicker = styled.input`
  height: 30px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

const ColorPreview = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid var(--semantic-border, #ced4da);
  background-color: ${props => props.color};

  html.dark & {
    border-color: var(--dark-300);
  }
`;

const ColorPickerContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const ColorsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const ColorButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--semantic-border, #ced4da);
  background-color: ${props => props.color};
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-500, #3b82f6);
  }

  html.dark & {
    border-color: var(--dark-300);

    &:focus {
      box-shadow: 0 0 0 2px var(--accent-500);
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  border: 1px solid var(--semantic-border, #ced4da);
  background-color: ${props => props.primary ? 'var(--primary-600, #2563eb)' : 'var(--semantic-surface, #ffffff)'};
  color: ${props => props.primary ? 'white' : 'var(--semantic-text-secondary, #6c757d)'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.primary ? 'var(--primary-700, #1d4ed8)' : 'var(--primary-50, #eff6ff)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  html.dark & {
    border-color: var(--dark-300);
    background-color: ${props => props.primary ? 'var(--accent-500)' : 'var(--dark-200)'};
    color: ${props => props.primary ? 'white' : 'var(--dark-700)'};

    &:hover:not(:disabled) {
      background-color: ${props => props.primary ? 'var(--accent-600)' : 'var(--dark-300)'};
    }
  }
`;

const CustomDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--semantic-surface, #ffffff);
  color: var(--semantic-text-primary, #333);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-400, #60a5fa);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid var(--semantic-text-secondary, #6c757d);
    pointer-events: none;
  }

  html.dark & {
    background-color: var(--dark-200);
    border-color: var(--dark-300);
    color: var(--dark-900);

    &:hover {
      border-color: var(--accent-400);
    }

    &:after {
      border-top-color: var(--dark-700);
    }
  }
`;

const SelectedOption = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background-color: var(--semantic-surface, #ffffff);
  border: 1px solid var(--semantic-border, #ced4da);
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin-top: 2px;

  html.dark & {
    background-color: var(--dark-100);
    border-color: var(--dark-300);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  }
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  color: var(--semantic-text-primary, #333);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary-50, #eff6ff);
  }
  
  ${props => props.selected && `
    background-color: var(--primary-100, #dbeafe);
    font-weight: 500;
  `}

  html.dark & {
    color: var(--dark-900);

    &:hover {
      background-color: var(--dark-200);
    }

    ${props => props.selected && `
      background-color: var(--accent-500/10);
      color: var(--accent-400);
    `}
  }
`;

const CategoryColorDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color || '#ccc'};
  flex-shrink: 0;
  border: 1px solid var(--semantic-border, #e5e7eb);

  html.dark & {
    border-color: var(--dark-300);
  }
`;

const CreateCategoryItem = styled(DropdownItem)`
  color: var(--primary-600, #2563eb);
  
  &:hover {
    background-color: var(--primary-50, #eff6ff);
  }

  html.dark & {
    color: var(--accent-500);

    &:hover {
      background-color: var(--accent-500/10);
    }
  }
`;

const TaskForm = ({ task, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    category_id: null,
    task_type: 'ThisWeek',
    is_high_priority: false,
    is_recurring: false,
    scheduled_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    recurring_formula: ''
  });
  
  const [categories, setCategories] = useState([]);
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [selectedRecurrencePreset, setSelectedRecurrencePreset] = useState(null);
  const [showCustomRecurrence, setShowCustomRecurrence] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#a0d2eb'
  });
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Pastel colors for quick selection
  const pastelColors = useMemo(() => [
    { name: 'Pastel Blue', value: '#a0d2eb' },
    { name: 'Pastel Purple', value: '#d0bdf4' },
    { name: 'Pastel Pink', value: '#f4c2c2' },
    { name: 'Pastel Green', value: '#b5ead7' },
    { name: 'Pastel Yellow', value: '#fef9e7' },
    { name: 'Pastel Orange', value: '#fdcb6e' },
    { name: 'Pastel Mint', value: '#c7f9cc' },
    { name: 'Pastel Lavender', value: '#d4c1ec' },
    { name: 'Pastel Peach', value: '#fad0c3' },
    { name: 'Pastel Teal', value: '#98ddca' }
  ], []);
  
  // Time picker options
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return options;
  }, []);
  
  // Add refs for date and time picker containers
  const datePickerRef = useRef(null);
  const startTimePickerRef = useRef(null);
  const endTimePickerRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Use the custom hook to handle outside clicks
  useClickOutside(datePickerRef, () => {
    setShowDatePicker(false);
  });
  
  useClickOutside(startTimePickerRef, () => {
    setShowStartTimePicker(false);
  });
  
  useClickOutside(endTimePickerRef, () => {
    setShowEndTimePicker(false);
  });
  
  // Close category dropdown when clicking outside
  useClickOutside(categoryDropdownRef, () => {
    setShowCategoryDropdown(false);
  });
  
  // Load categories and initialize form data
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await taskService.getCategories();
        // Ensure categoriesData is an array, or provide a default empty array
        const categoriesArray = Array.isArray(categoriesData) ? categoriesData : 
          (categoriesData?.categories || []);
        setCategories(categoriesArray);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]); // Ensure categories is an array even on error
      }
    };
    
    loadCategories();
    
    //console.log('[TaskForm] useEffect triggered with task:', task);
    
    // Initialize form data if editing
    if (task) {
      //console.log('[TaskForm] Initializing form with task data:', task);
      setFormData({
        title: task.title || '',
        category_id: task.category?.id || null,
        task_type: task.task_type || 'ThisWeek',
        is_high_priority: task.is_high_priority || false,
        is_recurring: !!task.recurring_formula,
        scheduled_date: task.scheduled_date || new Date().toISOString().split('T')[0],
        start_time: task.start_time ? task.start_time.slice(11, 16) : '09:00',
        end_time: task.end_time ? task.end_time.slice(11, 16) : '10:00',
        recurring_formula: task.recurring_formula || ''
      });
      //console.log('[TaskForm] Form data set:', { ...formData }); // Log state *after* setting
      
      setShowRecurringOptions(!!task.recurring_formula);
      
      // Set recurrence preset if editing a recurring task
      if (task.recurring_formula) {
        const matchingPreset = RECURRENCE_PRESETS.find(
          preset => preset.value === task.recurring_formula
        );
        if (matchingPreset) {
          setSelectedRecurrencePreset(matchingPreset.value);
        } else {
          setSelectedRecurrencePreset('custom');
          setShowCustomRecurrence(true);
        }
      }
    } else {
      //console.log('[TaskForm] Initializing form for a new task (task prop is null/undefined).');
      // Reset form for a new task if task is null
      setFormData({
        title: 'New Task', // Default title for drag-created tasks
        category_id: null,
        task_type: 'ThisWeek',
        is_high_priority: false,
        is_recurring: false,
        scheduled_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '10:00',
        recurring_formula: ''
      });
    }
  }, [task]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle toggle inputs
  const handleToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    
    // If toggling recurring, show/hide recurring options
    if (field === 'is_recurring') {
      const newValue = !formData.is_recurring;
      setShowRecurringOptions(newValue);
      
      // If enabling recurring, set task_type to Recurring and set default recurrence
      if (newValue) {
        setFormData(prev => ({ 
          ...prev, 
          task_type: 'Recurring',
          recurring_formula: 'FREQ=WEEKLY'
        }));
        setSelectedRecurrencePreset('FREQ=WEEKLY');
      } else {
        setFormData(prev => ({ ...prev, recurring_formula: '' }));
        setSelectedRecurrencePreset(null);
        setShowCustomRecurrence(false);
      }
    }
  };
  
  // Handle task type change
  const handleTaskTypeChange = (e) => {
    const taskType = e.target.value;
    setFormData(prev => ({ ...prev, task_type: taskType }));
    
    // If switching to Recurring, enable recurring option
    if (taskType === 'Recurring' && !formData.is_recurring) {
      setFormData(prev => ({ 
        ...prev, 
        is_recurring: true,
        recurring_formula: 'FREQ=WEEKLY'
      }));
      setShowRecurringOptions(true);
      setSelectedRecurrencePreset('FREQ=WEEKLY');
    }
  };
  
  // Handle date change
  const handleDateChange = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, scheduled_date: e.target.value }));
    // Don't close the picker after changing the date - allow continuous typing
  };
  
  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    setFormData(prev => ({ ...prev, scheduled_date: formattedDate }));
    setShowDatePicker(false);
  };
  
  // Handle quick date selection
  const handleQuickDateSelect = (option, e) => {
    if (e) {
      e.stopPropagation();
    }
    let date = new Date();
    
    switch(option) {
      case 'tomorrow':
        date = addDays(date, 1);
        break;
      case 'nextWeek':
        date = addWeeks(date, 1);
        break;
      case 'nextMonth':
        date = addMonths(date, 1);
        break;
      // Default is today
    }
    
    const formattedDate = format(date, 'yyyy-MM-dd');
    setFormData(prev => ({ ...prev, scheduled_date: formattedDate }));
    setShowDatePicker(false);
  };
  
  // Handle time selection
  const handleTimeSelect = (time, isStart, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (isStart) {
      setFormData(prev => ({ ...prev, start_time: time }));
      setShowStartTimePicker(false);
    } else {
      setFormData(prev => ({ ...prev, end_time: time }));
      setShowEndTimePicker(false);
    }
  };
  
  // Update end time based on duration button
  const handleDurationClick = (minutes) => {
    const startTime = formData.start_time;
    const [hours, mins] = startTime.split(':').map(Number);
    
    const startDate = new Date();
    startDate.setHours(hours, mins, 0);
    
    const endDate = new Date(startDate.getTime() + minutes * 60000);
    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMins = endDate.getMinutes().toString().padStart(2, '0');
    
    setFormData(prev => ({ ...prev, end_time: `${endHours}:${endMins}` }));
  };
  
  // Handle recurrence preset selection
  const handleRecurrenceSelect = (value, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (value === 'custom') {
      setShowCustomRecurrence(true);
      setSelectedRecurrencePreset(value);
      // Don't update the formula yet, wait for custom input
    } else {
      setShowCustomRecurrence(false);
      setSelectedRecurrencePreset(value);
      setFormData(prev => ({ ...prev, recurring_formula: value }));
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const taskData = {
        ...formData,
        category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
        // Format date/time fields based on task type
        scheduled_date: 
          formData.task_type === 'Scheduled' || formData.task_type === 'Recurring' 
            ? formData.scheduled_date 
            : null,
        // Format to YYYY-MM-DDTHH:mm:SS
        start_time: 
          formData.task_type === 'Scheduled' || formData.task_type === 'Recurring'
            ? `${formData.scheduled_date}T${formData.start_time}:00` 
            : null,
        end_time: 
          formData.task_type === 'Scheduled' || formData.task_type === 'Recurring'
            ? `${formData.scheduled_date}T${formData.end_time}:00` 
            : null,
        recurring_formula: formData.is_recurring ? formData.recurring_formula : null
      };
      
      // Remove category_id if null
      if (taskData.category_id === null) {
        delete taskData.category_id;
      }
      
      // Remove is_recurring helper field before sending
      delete taskData.is_recurring;

      //console.log("Submitting Task Data:", taskData);

      // Save task
      if (task && task.id) {
        // Preserve notes_link when updating an existing task
        await taskService.updateTask(task.id, {
          ...taskData,
          notes_link: task.notes_link || null
        });
      } else {
        await taskService.createTask(taskData);
      }
      
      // Callback to parent component
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(`Failed to save task: ${error.message || 'Please check details'}`);
    }
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'EEE, MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };
  
  // Function to create a new category
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    setCreatingCategory(true);
    try {
      const response = await taskService.createCategory({
        name: newCategory.name,
        color: newCategory.color
      });
      
      // Add new category to the list and select it
      const newCategoryWithId = {
        id: response.id || response.category_id,
        name: newCategory.name,
        color: newCategory.color
      };
      
      setCategories([...categories, newCategoryWithId]);
      setFormData(prev => ({ ...prev, category_id: String(newCategoryWithId.id) }));
      
      // Reset form
      setNewCategory({ name: '', color: '#a0d2eb' });
      
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(`Failed to create category: ${error.message || 'Please try again'}`);
    } finally {
      setCreatingCategory(false);
    }
  };
  
  // Function to handle color selection
  const handleColorSelect = (color) => {
    setNewCategory(prev => ({ ...prev, color }));
  };
  
  // Function to handle category selection
  const handleCategorySelect = (id) => {
    setFormData(prev => ({ ...prev, category_id: id }));
    setShowCategoryDropdown(false);
  };
  
  return (
    <FormContainer>
      <FormHeader>{task ? 'Edit Task' : 'Add New Task'}</FormHeader>
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Input
            name="title"
            placeholder="Title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Category</Label>
          <CustomDropdown ref={categoryDropdownRef}>
            <DropdownTrigger onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}>
              <SelectedOption>
                {formData.category_id ? (
                  <>
                    <CategoryColorDot 
                      color={(categories.find(cat => cat.id.toString() === formData.category_id.toString())?.color) || '#ccc'} 
                    />
                    {(categories.find(cat => cat.id.toString() === formData.category_id.toString())?.name) || 'Select Category'} 
                  </>
                ) : (
                  'No Category'
                )}
              </SelectedOption>
            </DropdownTrigger>
            
            {showCategoryDropdown && (
              <DropdownMenu>
                <DropdownItem 
                  selected={formData.category_id === ''}
                  onClick={() => handleCategorySelect('')}
                >
                  No Category
                </DropdownItem>
                
                {categories.map(category => (
                  <DropdownItem 
                    key={category.id} 
                    selected={formData.category_id === category.id.toString()}
                    onClick={() => handleCategorySelect(category.id.toString())}
                  >
                    <CategoryColorDot color={category.color} />
                    {category.name}
                  </DropdownItem>
                ))}
                
                <CreateCategoryItem onClick={() => handleCategorySelect('new')}>
                  <FaPlus style={{ marginRight: '8px' }} />
                  Create New Category
                </CreateCategoryItem>
              </DropdownMenu>
            )}
          </CustomDropdown>
          
          {formData.category_id === 'new' && (
            <NewCategoryForm>
              <FormGroup>
                <Label>Category Name</Label>
                <Input
                  value={newCategory.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter category name"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Category Color</Label>
                <ColorPickerContainer>
                  <ColorPreview color={newCategory.color} />
                  <ColorPicker
                    type="color"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  />
                </ColorPickerContainer>
                
                <ColorsGrid>
                  {pastelColors.map(color => (
                    <ColorButton
                      key={color.value}
                      type="button"
                      color={color.value}
                      onClick={() => handleColorSelect(color.value)}
                      title={color.name}
                    />
                  ))}
                </ColorsGrid>
              </FormGroup>
              
              <FormActions>
                <ActionButton 
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category_id: '' }))}
                >
                  Cancel
                </ActionButton>
                <ActionButton 
                  type="button"
                  primary
                  onClick={handleCreateCategory}
                  disabled={creatingCategory}
                >
                  {creatingCategory ? 'Creating...' : 'Create Category'}
                </ActionButton>
              </FormActions>
            </NewCategoryForm>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label>Schedule Type</Label>
          <SelectContainer>
            <Select 
              name="task_type" 
              value={formData.task_type}
              onChange={handleTaskTypeChange}
            >
              <option value="ThisWeek">This Week</option>
              <option value="Waitlist">Waitlist</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Recurring">Recurring</option>
            </Select>
          </SelectContainer>
        </FormGroup>
        
        {(formData.task_type === 'Scheduled' || formData.task_type === 'Recurring') && (
          <>
            <DateTimeContainer>
              <DateTimeGroup>
                <Label>Date</Label>
                <DatePickerContainer ref={datePickerRef}>
                  <DateTimeInput onClick={(e) => {
                    e.stopPropagation();
                    setShowDatePicker(prev => !prev);
                  }}>
                    <FaCalendarAlt />
                    {formatDisplayDate(formData.scheduled_date)}
                  </DateTimeInput>
                  
                  {showDatePicker && (
                    <DatePickerDropdown>
                      <div style={{ padding: '8px' }}>
                        <DateTimeInput
                          as="input"
                          type="date"
                          value={formData.scheduled_date}
                          onChange={handleDateChange}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                          onFocus={e => e.stopPropagation()}
                          autoFocus
                        />
                        
                        <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                          <Button 
                            type="button" 
                            onClick={(e) => handleQuickDateSelect('today', e)}
                            style={{ padding: '4px', fontSize: '0.875rem' }}
                          >
                            Today
                          </Button>
                          <Button 
                            type="button" 
                            onClick={(e) => handleQuickDateSelect('tomorrow', e)}
                            style={{ padding: '4px', fontSize: '0.875rem' }}
                          >
                            Tomorrow
                          </Button>
                          <Button 
                            type="button" 
                            onClick={(e) => handleQuickDateSelect('nextWeek', e)}
                            style={{ padding: '4px', fontSize: '0.875rem' }}
                          >
                            Next Week
                          </Button>
                          <Button 
                            type="button" 
                            onClick={(e) => handleQuickDateSelect('nextMonth', e)}
                            style={{ padding: '4px', fontSize: '0.875rem' }}
                          >
                            Next Month
                          </Button>
                        </div>
                      </div>
                    </DatePickerDropdown>
                  )}
                </DatePickerContainer>
              </DateTimeGroup>
            </DateTimeContainer>
            
            <DateTimeContainer>
              <DateTimeGroup>
                <Label>Start Time</Label>
                <DatePickerContainer ref={startTimePickerRef}>
                  <DateTimeInput onClick={(e) => {
                    e.stopPropagation();
                    setShowStartTimePicker(prev => !prev);
                  }}>
                    <FaClock />
                    {formData.start_time}
                  </DateTimeInput>
                  
                  {showStartTimePicker && (
                    <DatePickerDropdown>
                      <TimePickerGrid>
                        {timeOptions.map(time => (
                          <TimeOption 
                            key={time} 
                            selected={time === formData.start_time}
                            onClick={(e) => handleTimeSelect(time, true, e)}
                          >
                            {time}
                          </TimeOption>
                        ))}
                      </TimePickerGrid>
                    </DatePickerDropdown>
                  )}
                </DatePickerContainer>
              </DateTimeGroup>
              
              <DateTimeGroup>
                <Label>End Time</Label>
                <DatePickerContainer ref={endTimePickerRef}>
                  <DateTimeInput onClick={(e) => {
                    e.stopPropagation();
                    setShowEndTimePicker(prev => !prev);
                  }}>
                    <FaClock />
                    {formData.end_time}
                  </DateTimeInput>
                  
                  {showEndTimePicker && (
                    <DatePickerDropdown>
                      <TimePickerGrid>
                        {timeOptions.map(time => (
                          <TimeOption 
                            key={time} 
                            selected={time === formData.end_time}
                            onClick={(e) => handleTimeSelect(time, false, e)}
                          >
                            {time}
                          </TimeOption>
                        ))}
                      </TimePickerGrid>
                    </DatePickerDropdown>
                  )}
                </DatePickerContainer>
                
                <DurationButtons>
                  <DurationButton type="button" onClick={() => handleDurationClick(15)}>+15m</DurationButton>
                  <DurationButton type="button" onClick={() => handleDurationClick(30)}>+30m</DurationButton>
                  <DurationButton type="button" onClick={() => handleDurationClick(60)}>+1h</DurationButton>
                </DurationButtons>
              </DateTimeGroup>
            </DateTimeContainer>
          </>
        )}
        
        <ToggleContainer>
          <ToggleLabel>High Priority</ToggleLabel>
          <Toggle 
            active={formData.is_high_priority} 
            onClick={() => handleToggle('is_high_priority')}
          >
            <ToggleButton active={formData.is_high_priority} />
          </Toggle>
        </ToggleContainer>
        
        <ToggleContainer>
          <ToggleLabel>Recurring Task</ToggleLabel>
          <Toggle 
            active={formData.is_recurring} 
            onClick={() => handleToggle('is_recurring')}
          >
            <ToggleButton active={formData.is_recurring} />
          </Toggle>
        </ToggleContainer>
        
        {showRecurringOptions && (
          <RecurrenceOptionContainer>
            <RecurrenceHeader>
              <FaRedo />
              <span>Recurrence</span>
            </RecurrenceHeader>
            
            <RecurrenceGrid>
              {RECURRENCE_PRESETS.map(option => (
                <RecurrenceOption 
                  key={option.value}
                  selected={selectedRecurrencePreset === option.value}
                  onClick={(e) => handleRecurrenceSelect(option.value, e)}
                >
                  {option.label}
                </RecurrenceOption>
              ))}
            </RecurrenceGrid>
            
            {showCustomRecurrence && (
              <CustomRecurrenceForm>
                <Label>Custom ICAL Formula</Label>
                <Input
                  name="recurring_formula"
                  placeholder="e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR"
                  value={formData.recurring_formula}
                  onChange={handleChange}
                  required={formData.is_recurring && showCustomRecurrence}
                />
                <HelpText>
                  <FaInfoCircle />
                  Common examples: Daily (FREQ=DAILY), Weekly on Monday (FREQ=WEEKLY;BYDAY=MO), 
                  Monthly (FREQ=MONTHLY), Yearly (FREQ=YEARLY)
                </HelpText>
              </CustomRecurrenceForm>
            )}
            
            {formData.recurring_formula && (
              <ICALResult>
                {formData.recurring_formula}
              </ICALResult>
            )}
          </RecurrenceOptionContainer>
        )}
        
        <ButtonContainer>
          <CancelButton type="button" onClick={onCancel}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit">
            {task ? 'Update Task' : 'Add Task'}
          </SubmitButton>
        </ButtonContainer>
      </form>
    </FormContainer>
  );
};

export default TaskForm; 